import ListItemButton from "@mui/material/ListItemButton";
import { styled } from "@mui/material/styles";
import {
  chooseListChip,
  type ListChipCounts,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";

// #region Styled Components
/** A column inside the list's surface, the full height of the list, with a hairline before the table. */
const Rail = styled("nav")(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flex: "0 0 200px",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  padding: theme.spacing(2, 1),
}));
const RailItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  // A list button grows to fill a flex column; in the rail, which is as tall as the list, that spread
  // the folders down its whole height.
  flexGrow: 0,
  fontSize: "0.875rem",
  justifyContent: "space-between",
  paddingBlock: theme.spacing(0.75),
  "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 500 },
}));
const RailCount = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  marginLeft: theme.spacing(1),
}));
// #endregion

/**
 * One chip group as a column of folders beside the list, for a pane wide enough to spare it (#6720).
 * The categories of Documents read like the folders of a file manager: each is one press, and every
 * count is in view without opening a menu.
 *
 * A folder lists its value alone, as a folder does; pressing the chosen one again, or the first item,
 * lists everything. A link that chose several values shows each of them chosen. On a narrower pane the
 * page draws the same group as `ListChips`' menu, which can choose several.
 */
export default function ListChipRail(
  props: Readonly<{
    group: ListChipGroup;
    groups: readonly ListChipGroup[];
    selection: ListChipSelection;
    onChange: (selection: ListChipSelection) => void;
    /** What the first item says: "All documents". */
    allLabel: string;
    /** How many rows the list holds with nothing chosen in the group; none while it is on the wire. */
    allCount?: number;
    counts?: ListChipCounts;
  }>,
) {
  const { group, groups, selection, onChange, allLabel, allCount, counts } = props;
  const chosen = selection[group.parameter] ?? [];
  const choose = (value: string | undefined) => onChange(chooseListChip(groups, selection, group.parameter, value));
  return (
    <Rail aria-label={group.label}>
      <RailItem selected={chosen.length === 0} aria-current={chosen.length === 0} onClick={() => choose(undefined)}>
        {allLabel}
        {allCount !== undefined && <RailCount>{allCount}</RailCount>}
      </RailItem>
      {group.chips.map((chip) => {
        const isChosen = chosen.includes(chip.value);
        const count = counts?.[group.parameter]?.[chip.value];
        return (
          <RailItem
            key={chip.value}
            selected={isChosen}
            aria-current={isChosen}
            onClick={() => choose(isChosen && chosen.length === 1 ? undefined : chip.value)}
          >
            {chip.label}
            {count !== undefined && <RailCount>{count}</RailCount>}
          </RailItem>
        );
      })}
    </Rail>
  );
}
